import { GROUP_CALENDAR_ALWAYS_ON_ROLES } from '../duna-org-settings/duna-org-settings.component';
import { EducationalPathUserDTO } from '../../../dto/educational-path/educational-path-user.dto';

export interface GroupCalendarOrgConfig {
  groupCalendarEnabled: boolean;
  showAllLocations: boolean;
  viewRoles: string[];
  editPathIds: number[];
}

/**
 * Returns the effective view roles: config roles plus always-on roles and superadmin.
 */
export function resolveEffectiveViewRoles(configRoles: string[]): string[] {
  const roles = new Set(configRoles);
  for (const r of GROUP_CALENDAR_ALWAYS_ON_ROLES) {
    roles.add(r);
  }
  roles.add('superadmin');
  return Array.from(roles);
}

/**
 * Returns whether the user can see the user-group calendar based on org settings.
 * userRoles: flat list of roles from JWT (e.g. ['uddansvarlig', 'orgbruger'])
 * userInfo: decoded user_info from JWT (may contain primaer/sekundaer arrays)
 */
export function userHasViewRole(
  userRoles: string[],
  userInfo: Record<string, unknown>,
  viewRoles: string[]
): boolean {
  const effective = new Set(resolveEffectiveViewRoles(viewRoles));
  for (const role of userRoles) {
    if (effective.has(role)) return true;
  }
  // For primaer/sekundaer, check if the user_info arrays are non-empty
  // (JWT stores location names under these keys when the user has that role)
  if (effective.has('primaer')) {
    const arr = userInfo['primaer'];
    if (Array.isArray(arr) && arr.length > 0) return true;
  }
  if (effective.has('sekundaer')) {
    const arr = userInfo['sekundaer'];
    if (Array.isArray(arr) && arr.length > 0) return true;
  }
  return false;
}

/** Path IDs from JWT user_info when present; null means caller must load paths from API. */
export function resolveUserPathIdsFromUserInfo(userInfo: Record<string, unknown>): number[] | null {
  const raw = userInfo['educational_path_ids'];
  if (Array.isArray(raw)) {
    return raw.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0);
  }
  return null;
}

export function extractPathIdsFromEducationalPaths(paths: EducationalPathUserDTO[]): number[] {
  return paths
    .map((p) => p.path_id)
    .filter((id): id is number => typeof id === 'number' && id > 0);
}

/**
 * View access: configured view roles, or membership in a group_calendar_edit_path_ids uddannelsesvej.
 */
export function userCanViewGroupCalendar(params: {
  userRoles: string[];
  userInfo: Record<string, unknown>;
  viewRoles: string[];
  editPathIds: number[];
  userPathIds: number[];
}): boolean {
  const { userRoles, userInfo, viewRoles, editPathIds, userPathIds } = params;
  if (userHasViewRole(userRoles, userInfo, viewRoles)) {
    return true;
  }
  if (editPathIds.length === 0 || userPathIds.length === 0) {
    return false;
  }
  return userPathIds.some((id) => editPathIds.includes(id));
}

/**
 * Filters the calendar location guids based on org scope setting and the user's own locations.
 * showAll: org setting — if true, show all configured guids
 * allGuids: guids from group_calendar_settings
 * userLocationGuids: guids from GET /users/locations
 */
export function filterCalendarLocationGuids(
  allGuids: string[],
  userLocationGuids: string[],
  showAll: boolean
): string[] {
  if (showAll) return allGuids;
  const userSet = new Set(userLocationGuids);
  return allGuids.filter((guid) => userSet.has(guid));
}

export interface CanEditGroupCalendarParams {
  userRoles: string[];
  userPathIds: number[];
  editPathIds: number[];
  locationKey: string;
  userLocationGuids: string[];
}

/**
 * Returns whether the user can edit events in the given calendar column.
 * - superadmin/orgadmin/orgbruger always can edit all columns
 * - Otherwise: user must have at least one path_id in editPathIds AND the column must be one
 *   of the user's own locations (or 'other', which is global for path-users with ≥1 location)
 */
export function userCanEditGroupCalendar(params: CanEditGroupCalendarParams): boolean {
  const { userRoles, userPathIds, editPathIds, locationKey, userLocationGuids } = params;

  if (
    userRoles.includes('superadmin') ||
    userRoles.includes('orgadmin') ||
    userRoles.includes('orgbruger')
  ) {
    return true;
  }

  if (editPathIds.length === 0) return false;
  const hasEditPath = userPathIds.some((id) => editPathIds.includes(id));
  if (!hasEditPath) return false;

  if (locationKey === 'other') {
    return userLocationGuids.length > 0;
  }
  return userLocationGuids.includes(locationKey);
}
